import { mapImageConstants } from "../../utils/imageMapper";

interface RichContentCardProps {
  title: string;
  titleStyle?: string;
  description: string;
  image: string;
  videos?: string[];
  viewPostUrl?: string | string[];
}

export default function RichContentCard({
  title,
  titleStyle,
  description,
  image,
  videos,
  viewPostUrl,
}: RichContentCardProps) {
  const imageUrl = mapImageConstants({ image }).image;

  return (
    <div className="w-full">
      {/* Image at Top */}
      {imageUrl ? (
        <div className="mb-8">
          <img
            src={imageUrl}
            alt={title}
            className="w-full object-contain rounded-lg transition-transform duration-300 hover:scale-105"
          />
        </div>
      ) : null}

      {/* Description Section */}
      <div className="space-y-4">
        <h3
          className={`text-xl md:text-[32px] leading-snug font-semibold text-gray-200 ${titleStyle}`}
        >
          {title}
        </h3>
        <div className="space-y-2">
          {description.split("\n").map((paragraph, index) => {
            if (paragraph.trim() === "") return null;

            // Check if paragraph contains HTML tags
            if (paragraph.includes("<ul>") || paragraph.includes("<ol>")) {
              // Process HTML content and add video links
              let processedHtml = paragraph
                .replace(
                  /<ul>/g,
                  '<ul class="list-disc list-inside space-y-2 my-4 px-2">'
                )
                .replace(
                  /<ol>/g,
                  '<ol class="list-decimal list-inside space-y-2 px-2">'
                )
                .replace(/<li>/g, '<li class="text-xl leading-relaxed py-1">');

              // Add video links to "Watch Now" text within HTML
              if (videos && videos.length > 0) {
                let watchNowCount = 0;
                processedHtml = processedHtml.replace(/Watch Now/g, (match) => {
                  const videoUrl = videos[watchNowCount] || "#";
                  watchNowCount++;
                  return `<a href="${videoUrl}" class="hover:text-blue-300 underline text-xl" target="_blank" rel="noopener noreferrer">${match}</a>`;
                });
              }

              // Add "Read it here" links within HTML
              if (viewPostUrl && Array.isArray(viewPostUrl)) {
                let readItHereCount = 0;
                processedHtml = processedHtml.replace(
                  /Read it here/g,
                  (match) => {
                    const readUrl = viewPostUrl[readItHereCount] || "#";
                    readItHereCount++;
                    return `<a href="${readUrl}" class="hover:text-blue-300 underline text-xl" target="_blank" rel="noopener noreferrer">${match}</a>`;
                  }
                );
              }

              return (
                <div
                  key={index}
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: processedHtml }}
                />
              );
            }

            // Check if paragraph contains links
            if (
              paragraph.includes("@") ||
              paragraph.includes("http") ||
              paragraph.includes("Watch Now") ||
              paragraph.includes("View Post") ||
              paragraph.includes("Read it here") ||
              paragraph.includes("Learn more about the content")
            ) {
              const emailRegex =
                /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
              const urlRegex = /(https?:\/\/[^\s]+)/g;
              const watchNowRegex = /(Watch Now)/g;
              const viewPostRegex = /(View Post)/g;
              const readItHereRegex = /(Read it here)/g;
              const learnMoreRegex = /(Learn more about the content)/g;
              const combinedRegex =
                /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|https?:\/\/[^\s]+|Watch Now|View Post|Read it here|Learn more about the content)/g;
              const parts = paragraph.split(combinedRegex);

              // Track counts for different link types
              const linkCounts = {
                watchNow: 0,
                readItHere: 0,
                learnMore: 0
              };

              // Helper function to create link elements
              const createLink = (href: string, text: string, className: string, isExternal = true) => (
                <a
                  href={href}
                  className={className}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                >
                  {text}
                </a>
              );

              // Helper function to get URL for different link types
              const getLinkUrl = (linkType: string, part: string) => {
                switch (linkType) {
                  case 'email':
                    return `mailto:${part}`;
                  case 'url':
                    return part;
                  case 'watchNow':
                    const videoUrl = videos?.[linkCounts.watchNow] || '#';
                    linkCounts.watchNow++;
                    return videoUrl;
                  case 'viewPost':
                    return Array.isArray(viewPostUrl) ? viewPostUrl[0] : (viewPostUrl || '');
                  case 'readItHere':
                    const readUrls = Array.isArray(viewPostUrl) ? viewPostUrl : [viewPostUrl || '#'];
                    const readUrl = readUrls[linkCounts.readItHere] || '#';
                    linkCounts.readItHere++;
                    return readUrl;
                  case 'learnMore':
                    const learnMoreUrl = Array.isArray(viewPostUrl) 
                      ? viewPostUrl[linkCounts.learnMore] || '#'
                      : viewPostUrl || '#';
                    linkCounts.learnMore++;
                    return learnMoreUrl;
                  default:
                    return '#';
                }
              };

              return (
                <p key={index} className="text-lg leading-relaxed">
                  {parts.map((part) => {
                    if (!part) return null;
                    
                    // Determine link type and create appropriate link
                    if (emailRegex.test(part)) {
                      return createLink(
                        getLinkUrl('email', part),
                        part,
                        "hover:text-blue-300 underline text-xl"
                      );
                    } else if (urlRegex.test(part)) {
                      return createLink(
                        getLinkUrl('url', part),
                        part,
                        "hover:text-blue-300 underline text-xl"
                      );
                    } else if (watchNowRegex.test(part)) {
                      return createLink(
                        getLinkUrl('watchNow', part),
                        part,
                        "hover:text-blue-300 underline text-xl"
                      );
                    } else if (viewPostRegex.test(part)) {
                      return createLink(
                        getLinkUrl('viewPost', part),
                        part,
                        "hover:text-blue-300 underline text-xl"
                      );
                    } else if (readItHereRegex.test(part)) {
                      return createLink(
                        getLinkUrl('readItHere', part),
                        part,
                        "hover:text-blue-300 underline text-xl"
                      );
                    } else if (learnMoreRegex.test(part)) {
                      return createLink(
                        getLinkUrl('learnMore', part),
                        part,
                        "hover:text-blue-300 underline text-xl"
                      );
                    }
                    return part;
                  })}
                </p>
              );
            }

            return (
              <p key={index} className="text-xl leading-relaxed">
                {paragraph}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}
